/**
 * Typed API client.
 *
 * Auth: the backend requires a Shopify session token on every /api route
 * (see backend/src/middleware/auth.ts). Inside the Shopify admin iframe App
 * Bridge mints one per request; in local dev the backend accepts DEV_STORE_ID
 * instead, so no token is needed.
 *
 * Session tokens expire after ~1 minute, so we fetch a fresh one per request
 * rather than caching. That's the documented pattern, not an oversight.
 */

export type Platform =
  | 'meta' | 'google' | 'tiktok' | 'reddit' | 'pinterest'
  | 'x' | 'snapchat' | 'amazon' | 'youtube_shorts' | 'spotify' | 'linkedin';

/** Mirrors LIVE_PLATFORMS in the backend. The API 400s on anything else. */
export const LIVE_PLATFORMS = [
  'meta', 'google', 'pinterest',
] as const satisfies readonly Platform[];

export type LivePlatform = (typeof LIVE_PLATFORMS)[number];

export interface GeneratedCreatives {
  hooks: Array<Record<string, unknown>>;
  creatives: Array<Record<string, unknown>>;
}

export type Angle = 'problem' | 'transformation' | 'social_proof';

// ─────────────────────────────────────────────── response types

export interface Product {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  primary_image_url: string | null;
  price_cents: number;
  units_sold_30d: number;
  revenue_30d_cents: number;
  is_bestseller: 0 | 1;
  is_high_margin: 0 | 1;
  ad_fit_score: number;
}

export interface Store {
  id: string;
  shop_domain: string | null;
  name: string;
  source: 'shopify' | 'standalone';
}

export interface Campaign {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'ended';
  daily_budget_cents: number;
  product_title: string | null;
  spend_cents: number;
  revenue_cents: number;
  clicks: number;
  impressions: number;
  purchases: number;
  created_at: number;
}

export interface Recommendation {
  id: string;
  kind: 'scale' | 'kill' | 'new' | 'competitor_alert';
  title: string;
  rationale: string;
  expected_impact: string | null;
  payload_json: string;
  score: number;
  state: string;
  created_at: number;
}

export interface Hook {
  id: string;
  text: string;
  emotion: string | null;
  angle: string | null;
  global_ctr: number | null;
  global_roas: number | null;
  uses_count: number;
  source: string;
}

export interface DashboardSummary {
  kpis: {
    spend7_cents: number;
    revenue7_cents: number;
    orders7: number;
    roas7: number;
    cpa7_cents: number;
    ctr7: number;
    deltas: { spend: number; revenue: number; orders: number };
  };
  chartDaily: Array<{ day: string; platform: string; spend_cents: number; revenue_cents: number }>;
  platformSplit: Array<{ platform: string; spend_cents: number; revenue_cents: number }>;
  topCampaigns: Campaign[];
  topProducts: Product[];
  alerts: Recommendation[];
}

export interface CopilotReply {
  conversationId: string;
  reply: string;
  toolCalls: Array<{ name: string; input: unknown; output: unknown }>;
  pendingApprovals: Array<{
    id: string;
    kind: string;
    target: string;
    reason: string;
    spend_at_risk_cents: number;
  }>;
}

export interface CompetitorAd {
  id: string;
  brand: string;
  platform: string;
  headline: string;
  primaryText: string | null;
  daysLive: number;
  engagementScore: number | null;
  industry: string | null;
}

export interface Benchmark {
  metric: string;
  yourValue: number;
  median: number;
  p75: number;
  p90: number;
  yourPercentile: number;
  cohort: string;
  sampleSize: number;
}

export interface OperatorAction {
  id: string;
  kind: string;
  target: string;
  delta: number | null;
  rationale: string;
  state: string;
  applied_at: number | null;
  created_at: number;
}

export interface PlanDef {
  key: 'starter' | 'growth' | 'scale';
  name: string;
  priceUsd: number;
  trialDays: number;
  blurb: string;
}

export interface ShopifyBillingState {
  plan: 'starter' | 'growth' | 'scale';
  planName: string;
  priceUsd: number;
  status: string;
  trialEndsAt: number | null;
  hasActiveSubscription: boolean;
  availablePlans: PlanDef[];
  mtd: { ad_spend_cents: number; ad_revenue_cents: number };
}

export interface Account {
  storeId: string;
  userId: string;
  email: string;
  name: string | null;
  websiteUrl: string | null;
  plan: string;
  billingStatus: string;
  trialEndsAt: number | null;
  pixelVerified: boolean;
}

export interface AuthResponse {
  account: Account;
  token: string;
}

export interface PixelInstall {
  storeId: string;
  websiteUrl: string;
  pixelId: string;
  endpoint: string;
  snippet: string;
  purchaseSnippet: string;
  gtmNote: string;
}

export interface PixelStatus {
  installed: boolean;
  verified: boolean;
  pageViews24h: number;
  purchases24h: number;
  lastEventAt: number | null;
  blocker: string | null;
}

export interface ManualProduct {
  externalId?: string;
  title: string;
  description?: string;
  priceCents: number;
  costCents?: number;
  imageUrl?: string;
  productUrl?: string;
}

// ─────────────────────────────────────────────── auth

/** Shopify App Bridge, present when we're inside the admin iframe. */
interface AppBridge {
  idToken?: () => Promise<string>;
}
declare global {
  interface Window {
    shopify?: AppBridge;
  }
}

/**
 * Which product are we? The same bundle serves both.
 *
 * Embedded means Shopify put us in an iframe and passed `host`/`shop`. Anything
 * else is a standalone customer on our own domain. Deciding this once, here,
 * keeps the branch out of every component.
 */
export function isEmbedded(): boolean {
  const p = new URLSearchParams(window.location.search);
  if (p.get('host') || p.get('shop')) return true;
  try {
    return window.top !== window.self;
  } catch {
    return true; // cross-origin frame access threw — we are embedded
  }
}

const TOKEN_KEY = 'scaleads.session';

export const auth = {
  get token(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* private browsing — the session simply won't survive a reload */
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing to do */
    }
  },
  get isSignedIn(): boolean {
    return Boolean(this.token);
  },
};

/**
 * Resolve a credential for this request.
 *
 * Shopify session tokens expire after about a minute, so the embedded path
 * mints a fresh one per request rather than caching — that's the documented
 * pattern, not an oversight. The standalone token is long-lived and stored.
 */
async function sessionToken(): Promise<string | null> {
  if (isEmbedded()) {
    try {
      if (window.shopify?.idToken) return await window.shopify.idToken();
    } catch {
      /* fall through — dev mode runs unauthenticated via DEV_STORE_ID */
    }
    return null;
  }
  return auth.token;
}

/** Resolve the shop domain from the App Bridge host/shop param. */
export function currentShop(): string | null {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop');
  if (shop) return shop;
  const host = params.get('host');
  if (host) {
    try {
      return atob(host).split('/')[0];
    } catch {
      /* malformed host param */
    }
  }
  return null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE = import.meta.env.VITE_API_URL ?? '/api';

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await sessionToken();
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (!res.ok) {
    let code = 'unknown';
    let message = `${res.status} ${res.statusText}`;
    let requestId: string | undefined;
    try {
      const body = await res.json();
      code = body.error ?? code;
      message = body.message ?? message;
      requestId = body.request_id;
    } catch {
      /* non-JSON error body */
    }
    if (res.status === 401) {
      if (window.shopify) {
        console.warn('[api] session token rejected — App Bridge will re-issue');
      } else {
        // Standalone: the stored token is dead. Drop it so the router sends the
        // customer to sign in rather than looping on a credential the server
        // has already refused.
        auth.clear();
      }
    }
    throw new ApiError(res.status, code, message, requestId);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Store id used in route params.
 *
 * The backend cross-checks this against the authenticated session and 403s on
 * mismatch, so it is a convenience for readable URLs, not a security boundary —
 * setting it to someone else's id gets you a 403, not their data.
 *
 * It cannot be a module constant any more. An embedded Shopify app knows its
 * store at build time via env; a standalone customer's store is only known
 * after `/account/me` returns, which happens well after this module loads. So
 * it's mutable, and every route builder reads it through `sid()`.
 */
let currentStoreId: string =
  (import.meta.env.VITE_STORE_ID as string | undefined) ?? 's_acme_demo';

const sid = (): string => currentStoreId;

export function setStoreId(id: string): void {
  currentStoreId = id;
}

/**
 * Query string builder that drops empty values.
 *
 * `new URLSearchParams({ q: undefined })` serialises to `q=undefined` — the
 * literal string — which the backend then treats as a real filter and matches
 * nothing. Strip them here so an unset filter means "no filter".
 */
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  get storeId(): string {
    return sid();
  },

  // ── core
  stores: () => req<Store[]>('/stores'),
  dashboard: () => req<DashboardSummary>(`/dashboard/${sid()}/summary`),
  products: () => req<Product[]>(`/products/${sid()}`),
  product: (id: string) => req<Product>(`/products/${sid()}/${id}`),

  campaigns: (platform?: string) =>
    req<Campaign[]>(
      `/campaigns/${sid()}${platform && platform !== 'all' ? `?platform=${platform}` : ''}`,
    ),
  campaignDetail: (id: string) => req<Record<string, unknown>>(`/campaigns/${sid()}/${id}`),
  pauseCampaign: (id: string) =>
    req<{ ok: true }>(`/campaigns/${sid()}/${id}/pause`, { method: 'POST' }),
  resumeCampaign: (id: string) =>
    req<{ ok: true }>(`/campaigns/${sid()}/${id}/resume`, { method: 'POST', body: JSON.stringify({ merchantApproved: true }) }),
  oneClickLaunch: (body: {
    productId: string;
    dailyBudgetCents: number;
    totalBudgetCents: number;
    merchantApproved: true;
    channels: LivePlatform[];
    emotion?: string;
  }) =>
    req<Record<string, unknown>>(`/campaigns/${sid()}/oneclick`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  creatives: () => req<Record<string, unknown>[]>(`/creatives/${sid()}`),
  generateCreatives: (body: { productId: string; platform: LivePlatform; count?: number }) =>
    req<GeneratedCreatives>(`/creatives/${sid()}/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  creativeLibrary: (filters: { platform?: string; angle?: Angle; source?: string } = {}) =>
    req<Record<string, unknown>[]>(
      `/creative/${sid()}/library${qs(filters)}`,
    ),
  generateCreativesV2: (body: {
    productId: string;
    platforms: Platform[];
    perPlatform?: number;
    preferredAngles?: Angle[];
  }) =>
    req<Record<string, unknown>[]>(`/creative/${sid()}/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  remixCompetitor: (body: { competitorAdId: string; productId: string; platforms: Platform[] }) =>
    req<Record<string, unknown>[]>(`/creative/${sid()}/remix-competitor`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  hooks: (filters: { emotion?: string; platform?: string } = {}) =>
    req<Hook[]>(`/hooks${qs(filters)}`),
  hookWinners: () => req<Hook[]>(`/hooks/winners`),

  recommendations: (state = 'pending') =>
    req<Recommendation[]>(`/recommendations/${sid()}?state=${state}`),
  acceptRec: (id: string) =>
    req<{ applied: boolean }>(`/recommendations/${sid()}/${id}/accept`, { method: 'POST' }),
  dismissRec: (id: string) =>
    req<{ ok: true }>(`/recommendations/${sid()}/${id}/dismiss`, { method: 'POST' }),
  runOptimiser: () =>
    req<{ created: number }>(`/recommendations/${sid()}/run`, { method: 'POST' }),

  // ── reddit
  redditSubreddits: (productId?: string) =>
    req<{ subreddits: Record<string, unknown>[] }>(
      `/reddit/${sid()}/subreddits${productId ? `?productId=${productId}` : ''}`,
    ),
  redditInsights: () => req<Record<string, unknown>[]>(`/reddit/${sid()}/insights`),
  redditTemplates: (productId: string, subredditName: string) =>
    req<Record<string, unknown>>(`/reddit/${sid()}/templates`, {
      method: 'POST',
      body: JSON.stringify({ productId, subredditName }),
    }),

  // ── billing + connections
  billing: () => req<ShopifyBillingState>(`/billing/${sid()}`),
  /**
   * Starts a plan change. Returns a confirmationUrl — the plan has NOT changed.
   * The caller must navigate the merchant there; see Billing.tsx for why that
   * navigation has to escape the iframe.
   */
  changePlan: (plan: 'starter' | 'growth' | 'scale') =>
    req<{ confirmationUrl: string; requiresApproval: true; plan: string }>(
      `/billing/${sid()}/plan`,
      { method: 'POST', body: JSON.stringify({ plan }) },
    ),
  cancelPlan: () => req<{ cancelled: boolean }>(`/billing/${sid()}/cancel`, { method: 'POST' }),
  connections: () =>
    req<Array<{ platform: string; external_id: string; status: string; connected_at: number }>>(
      `/auth/connections/${sid()}`,
    ),
  platformCapabilities: () =>
    req<Record<'meta' | 'google' | 'pinterest', { enabled: boolean; configured: boolean }>>(
      `/auth/platforms/${sid()}`,
    ),
  startPlatformOAuth: (platform: 'google' | 'pinterest') =>
    req<{ authorizationUrl: string }>(`/auth/platform/${sid()}/${platform}/start`, { method: 'POST' }),
  platformSetupOptions: (platform: 'google' | 'pinterest') =>
    req<Record<string, Array<{ id: string; name: string }>>>(
      `/auth/platform/${sid()}/${platform}/options`,
    ),
  finishPlatformSetup: (platform: 'google' | 'pinterest', selection: Record<string, string>) =>
    req<{ ok: true; platform: string; status: 'active' }>(
      `/auth/platform/${sid()}/${platform}/setup`,
      { method: 'POST', body: JSON.stringify(selection) },
    ),
  connectPlatform: (body: {
    platform: string;
    externalId: string;
    credentials: Record<string, unknown>;
  }) => req<{ ok: true }>(`/auth/connect/${sid()}`, { method: 'POST', body: JSON.stringify(body) }),
  disconnectPlatform: (platform: string) =>
    req<{ ok: true }>(`/auth/connect/${sid()}/${platform}`, { method: 'DELETE' }),
  resync: () =>
    req<{ ok: true; jobId: string | null }>(`/auth/resync/${sid()}`, { method: 'POST' }),

  // ── ScaleAds Copilot
  copilotChat: (message: string, conversationId?: string) =>
    req<CopilotReply>(`/copilot/${sid()}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, conversationId }),
    }),
  approveCopilot: (approvalId: string) =>
    req<{ ok: true }>(`/copilot/${sid()}/approve/${approvalId}`, { method: 'POST' }),
  rejectCopilot: (approvalId: string) =>
    req<{ ok: true }>(`/copilot/${sid()}/reject/${approvalId}`, { method: 'POST' }),
  copilotConversations: () =>
    req<Array<{ id: string; title: string | null; updated_at: number }>>(
      `/copilot/${sid()}/conversations`,
    ),

  // ── competitor intelligence
  competitorSearch: (
    params: { q?: string; industry?: string; platform?: string; minDays?: number } = {},
  ) =>
    req<CompetitorAd[]>(
      `/competitors/${sid()}/search${qs(params)}`,
    ),
  trackCompetitor: (brand: string, platforms: string[]) =>
    req<{ id: string }>(`/competitors/${sid()}/track`, {
      method: 'POST',
      body: JSON.stringify({ brand, platforms }),
    }),

  // ── UGC marketplace
  createUgcBrief: (body: Record<string, unknown>) =>
    req<{ id: string }>(`/ugc/${sid()}/briefs`, { method: 'POST', body: JSON.stringify(body) }),
  recommendCreators: (briefId: string) =>
    req<Record<string, unknown>[]>(`/ugc/briefs/${briefId}/recommend`),
  reviewSubmission: (submissionId: string, accept: boolean, note?: string) =>
    req<{ ok: boolean; state: string }>(`/ugc/submissions/${submissionId}/review`, {
      method: 'POST',
      body: JSON.stringify({ accept, note, storeId: sid() }),
    }),

  // ── AI operator
  runOperator: (mode: 'recommend' | 'autonomous' = 'recommend') =>
    req<OperatorAction[]>(`/operator/${sid()}/run`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
  operatorDigest: (days = 7) =>
    req<{
      summary: string;
      actions: { applied: number; pending: number; reverted: number };
      topResults: string[];
    }>(`/operator/${sid()}/digest?days=${days}`),
  operatorActions: () => req<OperatorAction[]>(`/operator/${sid()}/actions`),

  // ── incrementality
  startIncrementality: (campaignId: string) =>
    req<{ id: string; treatment: string[]; control: string[]; durationDays: number }>(
      `/incrementality/${sid()}/start`,
      { method: 'POST', body: JSON.stringify({ campaignId }) },
    ),
  incrementalityOverview: () =>
    req<{
      campaigns: Array<{
        campaign_id: string;
        reported_roas: number;
        true_roas: number;
        multiplier: number;
      }>;
      blended_multiplier: number;
    }>(`/incrementality/${sid()}/overview`),

  // ── benchmarks
  benchmarks: (params: { industry?: string; platform?: string; priceBand?: string } = {}) =>
    req<Benchmark[]>(`/benchmarks/${sid()}${qs(params)}`),
  shareBenchmark: () =>
    req<{ imageUrl: string; caption: string }>(`/benchmarks/${sid()}/share`),

  // ── upsell
  aovUplift: () =>
    req<{
      baseline_aov_cents: number;
      with_upsell_aov_cents: number;
      uplift_pct: number;
      statistically_significant: boolean;
    }>(`/upsell/${sid()}/aov-uplift`),

  // ── account (standalone only)
  signup: (body: {
    email: string;
    password: string;
    websiteUrl: string;
    storeName?: string;
    name?: string;
    currency?: string;
  }) => req<AuthResponse>(`/account/signup`, { method: 'POST', body: JSON.stringify(body) }),

  login: (email: string, password: string) =>
    req<AuthResponse>(`/account/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  requestVerification: () =>
    req<{ ok: true; sent: boolean }>(`/account/verification/request`, { method: 'POST' }),

  confirmVerification: (token: string) =>
    req<{ ok: true }>(`/account/verification/confirm`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  forgotPassword: (email: string) =>
    req<{ ok: true }>(`/account/password/forgot`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    req<{ ok: true; signedOutEverywhere: boolean }>(`/account/password/reset`, {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  logout: () => req<void>(`/account/logout`, { method: 'POST' }),

  me: () =>
    req<{
      account: Account | null;
      storeId: string;
      source: 'shopify' | 'standalone';
      shop?: string | null;
    }>(`/account/me`),

  changePassword: (currentPassword: string, newPassword: string) =>
    req<{ ok: true; signedOutEverywhere: boolean }>(`/account/password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  // ── standalone billing (Stripe)
  billingAccess: () =>
    req<{ access: 'full' | 'read_only' | 'locked'; stripeConfigured: boolean }>(
      `/billing/${sid()}`,
    ),
  startCheckout: (plan: 'starter' | 'growth' | 'scale') =>
    req<{ url: string }>(`/billing/${sid()}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),
  billingPortal: () => req<{ url: string }>(`/billing/${sid()}/portal`, { method: 'POST' }),

  // ── pixel onboarding (standalone only)
  pixelInstructions: () => req<PixelInstall>(`/onboarding/${sid()}/pixel`),
  pixelStatus: () => req<PixelStatus>(`/onboarding/${sid()}/pixel/status`),

  // ── manual catalogue (standalone only)
  addProducts: (products: ManualProduct[]) =>
    req<{ created: number; updated: number }>(`/catalog/${sid()}/products`, {
      method: 'POST',
      body: JSON.stringify({ products }),
    }),
  importProductCsv: (csv: string) =>
    req<{ created: number; updated: number; parsed: number }>(`/catalog/${sid()}/import`, {
      method: 'POST',
      body: JSON.stringify({ csv }),
    }),

  // ── pixel health
  pixelRecovery: () =>
    req<{
      totalPurchases: number;
      metaDirect: number;
      recoveredViaCapi: number;
      recoveryRatePct: number;
    }>(`/pixel/${sid()}/recovery`),

  shopifyPixelStatus: () =>
    req<{
      scriptInstalled: boolean;
      pageViews24h: number;
      addToCarts24h: number;
      purchases24h: number;
      orderSignals24h: number;
      workerUrl: string;
    }>(`/pixel/${sid()}/shopify-status`),
  installShopifyPixel: () =>
    req<{ installed: boolean; src: string }>(`/pixel/${sid()}/install`, { method: 'POST' }),
};

// ─────────────────────────────────────────────── formatting

export const fmt = {
  money: (cents: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(cents / 100),
  moneyShort: (cents: number) => {
    const n = cents / 100;
    if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
    return `$${n.toFixed(2)}`;
  },
  num: (n: number) => new Intl.NumberFormat('en-AU').format(Math.round(n)),
  pct: (n: number, digits = 1) => `${(n * 100).toFixed(digits)}%`,
  delta: (n: number) => `${n >= 0 ? '+' : ''}${(n * 100).toFixed(1)}%`,
  roas: (revenue: number, spend: number) => (spend > 0 ? `${(revenue / spend).toFixed(2)}×` : '—'),
  relativeTime: (ms: number) => {
    const diff = Date.now() - ms;
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  },
};
